import React, { useState, useEffect } from "react";

const FULL_MESSAGE = "Nouvelle fonctionnalité du registre: Recherche par ID";
const VISIBLE_INDICES = [0, 9, 14, 18, 28]; // Quelques lettres visibles

export default function ErasedWhiteboardPuzzle({ onSolved, storageKey, hasUvLamp }) {
  const [uvActive, setUvActive] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [revealed, setRevealed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!uvActive || revealed) return;

    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setRevealed(true);
          try {
            localStorage.setItem(storageKey, "1");
          } catch { }
          setTimeout(() => onSolved?.(), 500);
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [uvActive, revealed, onSolved, storageKey]);

  const displayedMessage = FULL_MESSAGE.split("").map((char, i) => {
    if (char === " " || char === ":") return char;
    if (revealed || (uvActive && i <= (scanProgress / 100) * FULL_MESSAGE.length)) {
      return char;
    }
    if (VISIBLE_INDICES.includes(i)) return char;
    return "_";
  }).join("");

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-200">
        Un tableau blanc accroché au mur. On y lit :
      </p>

      <div className="flex justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl border-8 border-slate-300 p-8 w-full max-w-2xl">
          {/* Titre */}
          <div className="mb-8 text-center">
            <p className="text-6xl text-rose-500 font-bold" style={{ fontFamily: "'Pacifico', cursive" }}>
              I ❤️ RH
            </p>
          </div>

          <div className="border-t-2 border-slate-200 pt-6"></div>

          {/* Message avec effet UV */}
          <div className="relative">
            <p className="text-xs text-slate-400 mb-4 text-center italic">
              {revealed
                ? "✨ Message révélé !"
                : hasUvLamp && !uvActive
                  ? "Utilise la lampe UV pour lire le message effacé..."
                  : "Le texte est à moitié effacé, peut-être qu'un outil pourrait nous permettre de le lire..."}
            </p>

            {/* Zone de message */}
            <div
              className="relative min-h-[140px] rounded-xl overflow-hidden"
              style={{
                background: uvActive && !revealed
                  ? "linear-gradient(135deg, #1a0033 0%, #0a001a 100%)"
                  : "#f8fafc",
              }}
            >
              {/* Message */}
              <div className="relative p-8">
                <p
                  className="text-4xl font-bold text-center transition-all duration-500 whitespace-pre-wrap uppercase"
                  style={{
                    fontFamily: "'Caveat', cursive",
                    color: revealed
                      ? "#1e293b"
                      : uvActive
                        ? `rgba(139, 92, 246, ${Math.min(scanProgress / 100, 1)})`
                        : "rgba(100, 100, 100, 0.4)",
                    textShadow: uvActive && !revealed
                      ? `0 0 ${scanProgress / 10}px rgba(139, 92, 246, 0.8)`
                      : "none",
                    letterSpacing: "0.05em",
                  }}
                >
                  {displayedMessage.replace(":", " :\n")}
                </p>

                {/* Effet de scan UV */}
                {uvActive && !revealed && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: `linear-gradient(to bottom, 
                        transparent ${scanProgress}%, 
                        rgba(139, 92, 246, 0.3) ${scanProgress}%, 
                        transparent ${scanProgress + 5}%)`,
                      animation: "scan 2s linear infinite",
                    }}
                  />
                )}
              </div>

              {/* Particules UV */}
              {uvActive && !revealed && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-1 rounded-full bg-purple-400"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        opacity: Math.random() * 0.6,
                        animation: `float ${2 + Math.random() * 3}s ease-in-out infinite`,
                        animationDelay: `${Math.random() * 2}s`,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Bouton UV (seulement si lampe obtenue) */}
            {!revealed && hasUvLamp && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setUvActive(!uvActive)}
                  className={[
                    "px-8 py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all duration-300",
                    "border-2 shadow-lg",
                    uvActive
                      ? "bg-purple-600 border-purple-400 text-white shadow-purple-500/50"
                      : "bg-slate-200 border-slate-300 text-slate-700 hover:bg-slate-300",
                  ].join(" ")}
                >
                  {uvActive ? "🔦 UV activée" : "🔦 Activer la lampe UV"}
                </button>
              </div>
            )}

            {/* Barre de progression */}
            {uvActive && !revealed && (
              <div className="mt-4">
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all duration-100"
                    style={{
                      width: `${scanProgress}%`,
                      boxShadow: "0 0 10px rgba(139, 92, 246, 0.8)",
                    }}
                  />
                </div>
                <p className="text-xs text-purple-300 text-center mt-1">
                  Révélation en cours... {Math.round(scanProgress)}%
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {revealed && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4">
          <p className="text-sm font-semibold text-emerald-200 mb-2">
            ✅ Message révélé par lumière UV !
          </p>
          <p className="text-xs text-slate-300">
            Le registre RH permet de <span className="font-bold text-emerald-100">rechercher par ID</span> d'employé.
          </p>
        </div>
      )}

      <style jsx>{`
        @keyframes scan {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  );
}